import React, { useEffect, useState } from "react";
import { Plus, Pencil, Loader2, RefreshCw, QrCode, MapPin } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";

const empty = { name: "", address: "", latitude: -6.2, longitude: 106.816666, attendance_radius: 100, status: "aktif", auto_close_minutes: "" };

export default function OutletTab() {
  const [outlets, setOutlets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [qrOutlet, setQrOutlet] = useState(null);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setOutlets((await api.get("/outlets")).data); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (o) => {
    setForm({ name: o.name, address: o.address, latitude: o.latitude, longitude: o.longitude, attendance_radius: o.attendance_radius, status: o.status, auto_close_minutes: o.auto_close_minutes ?? "" });
    setEditing(o); setOpen(true);
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("GPS tidak tersedia");
    navigator.geolocation.getCurrentPosition(
      (pos) => { setForm((f) => ({ ...f, latitude: pos.coords.latitude, longitude: pos.coords.longitude })); toast.success("Lokasi terisi dari GPS"); },
      () => toast.error("Gagal mengambil lokasi"), { enableHighAccuracy: true }
    );
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, latitude: Number(form.latitude), longitude: Number(form.longitude), attendance_radius: Number(form.attendance_radius), auto_close_minutes: form.auto_close_minutes === "" ? null : Number(form.auto_close_minutes) };
      if (editing) await api.put(`/outlets/${editing.id}`, payload);
      else await api.post("/outlets", payload);
      toast.success("Outlet disimpan"); setOpen(false); await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const regenerate = async (o) => {
    try {
      const { data } = await api.post(`/outlets/${o.id}/regenerate-qr`);
      toast.success("QR Code diperbarui");
      await load();
      if (qrOutlet && qrOutlet.id === o.id) setQrOutlet(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#3368A0]" /></div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800">Data Outlet</h2>
        <Button data-testid="add-outlet-button" onClick={openAdd} className="bg-[#3368A0] hover:bg-[#284f7a] rounded-xl">
          <Plus className="h-4 w-4 mr-1.5" /> Tambah Outlet
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-4" data-testid="outlet-list">
        {outlets.map((o) => (
          <div key={o.id} className="bg-white rounded-2xl border border-slate-200 p-5 space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-bold text-slate-800">{o.name}</p>
                <p className="text-sm text-slate-500">{o.address}</p>
              </div>
              <StatusBadge status={o.status} />
            </div>
            <div className="flex items-center gap-2 text-xs text-slate-500 font-mono">
              <MapPin className="h-3.5 w-3.5 text-[#3f7f9c]" /> {o.latitude.toFixed(5)}, {o.longitude.toFixed(5)} · radius {o.attendance_radius}m
            </div>
            <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
              <Button size="sm" variant="outline" onClick={() => setQrOutlet(o)} data-testid={`view-qr-${o.id}`}><QrCode className="h-4 w-4 mr-1.5" /> QR Code</Button>
              <Button size="sm" variant="outline" onClick={() => regenerate(o)} data-testid={`regenerate-qr-button`}><RefreshCw className="h-4 w-4 mr-1.5" /> Regenerate</Button>
              <Button size="sm" variant="ghost" onClick={() => openEdit(o)} data-testid={`edit-outlet-${o.id}`}><Pencil className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Outlet" : "Tambah Outlet"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label>Nama Outlet</Label>
              <Input data-testid="outlet-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Alamat</Label>
              <Input data-testid="outlet-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Latitude</Label>
                <Input data-testid="outlet-lat-input" type="number" step="any" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Longitude</Label>
                <Input data-testid="outlet-lng-input" type="number" step="any" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} /></div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={useMyLocation} className="w-full">
              <MapPin className="h-4 w-4 mr-1.5" /> Gunakan Lokasi Saya
            </Button>
            <div className="space-y-1.5"><Label>Radius Absensi (meter)</Label>
              <Input data-testid="outlet-radius-input" type="number" value={form.attendance_radius} onChange={(e) => setForm({ ...form, attendance_radius: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Auto-Close Check-Out (menit, opsional)</Label>
              <Input data-testid="outlet-auto-close-input" type="number" min="0" value={form.auto_close_minutes}
                onChange={(e) => setForm({ ...form, auto_close_minutes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button data-testid="save-outlet-button" onClick={save} disabled={saving} className="bg-[#3368A0] hover:bg-[#284f7a]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!qrOutlet} onOpenChange={(v) => !v && setQrOutlet(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>QR Code — {qrOutlet?.name}</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center py-4">
            {qrOutlet && <img src={qrOutlet.qr_image} alt="QR" className="w-56 h-56" data-testid="outlet-qr-image" />}
            <p className="mt-3 text-sm font-mono text-slate-500">{qrOutlet?.qr_token}</p>
            <p className="mt-2 text-xs text-slate-400 text-center">Cetak dan tempel QR ini di outlet. Karyawan memindai untuk absensi.</p>
            <Button className="mt-4 bg-[#3368A0] hover:bg-[#284f7a]" onClick={() => regenerate(qrOutlet)}>
              <RefreshCw className="h-4 w-4 mr-1.5" /> Regenerate QR
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
